import { useEffect, useState } from "react";
import { dashboardService } from "../../services/api";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import StatCard from "../../components/StatCard/StatCard";
import StatusPill from "../../components/StatusPill/StatusPill";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { CollapsibleMetrics } from '../../components/MetricsToggle/MetricsToggle';

const fmtBRL = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingFeeId, setEditingFeeId] = useState(null);
    const { showError, showSuccess } = useNotification();
    const [filters, setFilters] = useState({
        username: "",
        email: "",
        start_date: "",
        end_date: "",
    });

    const [metrics, setMetrics] = useState({
        total_users: 0,
        new_users_count: 0,
        users_with_payments_count: 0,
        users_with_approved_payments_count: 0,
    });

    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadUsers(1);
    }, []);

    const loadUsers = async (page = 1) => {
        try {
            setLoading(true);

            const { data } = await dashboardService.getAdminUsers({
                page,
                ...filters,
            });

            setUsers(data.users || []);
            setMetrics({
                total_users: data.total_users,
                new_users_count: data.new_users_count,
                users_with_payments_count: data.users_with_payments_count,
                users_with_approved_payments_count:
                    data.users_with_approved_payments_count,
            });

            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        setFilters((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const applyFilters = () => {
        loadUsers(1);
    };

    const handleFeeChange = async (userId, newFee) => {
        try {
            const result = await dashboardService.updateUserFee(userId, { fee: newFee });
            setEditingFeeId(null);
            loadUsers(currentPage);
            if (result.data.success) {
                showSuccess("Taxa atualizada com sucesso!");
            }
        } catch (err) {
            console.error(err);
            showError("Erro ao atualizar taxa. " + (err?.response?.data?.error || err?.message || ''));
        }
    };

    const handleBan = async (user) => {
        const ok = window.confirm(
            `Banir ${user.username}?\n\nIsso vai:\n• Derrubar todas as sessões dele\n• Bloquear todos os bots dele\n• Banir os IPs conhecidos dele (impede novo cadastro)`
        );
        if (!ok) return;

        try {
            const { data } = await dashboardService.banUser(user.id);
            showSuccess(
                `Usuário banido: ${data.bots_blocked} bots bloqueados, ${data.ips_banned} IPs banidos.`
            );
            loadUsers(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao banir. " + (err?.response?.data?.error || err?.message || ''));
        }
    };

    const handleUnban = async (user) => {
        const ok = window.confirm(
            `Desbanir ${user.username}?\n\nEle volta a acessar a plataforma. Os bots dele continuam bloqueados (desbloqueie na aba Bots).`
        );
        if (!ok) return;

        try {
            await dashboardService.unbanUser(user.id);
            showSuccess("Usuário desbanido. Os bots dele permanecem bloqueados.");
            loadUsers(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao desbanir. " + (err?.response?.data?.error || err?.message || ''));
        }
    };

    return (
        <>
            {/* FILTROS */}
            <div className="filters-section">
                <div className="filters-grid">

                    <div className="filter-group">
                        <input
                            type="date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleFilterChange}
                            className="filter-input"
                        />
                    </div>
                    <div className="filter-group">

                        <input
                            type="date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleFilterChange}
                            className="filter-input"
                        />
                    </div><div className="filter-group">

                        <input
                            type="text"
                            name="username"
                            placeholder="Username"
                            value={filters.username}
                            onChange={handleFilterChange}
                            className="filter-input"
                        />
                    </div><div className="filter-group">

                        <input
                            type="text"
                            name="email"
                            placeholder="Email"
                            value={filters.email}
                            onChange={handleFilterChange}
                            className="filter-input"
                        />

                    </div>
                    <button onClick={applyFilters} className="btn-primary">
                        Aplicar Filtros
                    </button>
                </div>
            </div>

            {/* CARDS RESUMO */}
            <CollapsibleMetrics>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Usuários cadastrados" value={metrics.total_users} icon={{ class: 'fas fa-users', color: 'blue' }} />
                    <StatCard title="Novos (filtro)" value={metrics.new_users_count} icon={{ class: 'fas fa-user-plus', color: 'green' }} />
                    <StatCard
                        title="Usuários com pagamentos"
                        value={metrics.users_with_payments_count}
                        icon={{ class: 'fas fa-dollar-sign', color: 'purple' }}
                    />
                    <StatCard
                        title="Usuários com pagamentos aprovados"
                        value={metrics.users_with_approved_payments_count}
                        icon={{ class: 'fas fa-check-circle', color: 'red' }}
                    />
                </div>
            </CollapsibleMetrics>

            {/* CARDS DE USUÁRIOS */}
            <div className="admin-cards">
                {users.map((user) => {
                    const banned = !user.is_active;

                    return (
                        <div key={user.id} className="admin-card">
                            <div className="admin-card-head">
                                <div className="admin-avatar">{(user.username || '?').charAt(0)}</div>

                                <div className="admin-card-id">
                                    <div className="admin-card-name-row">
                                        <span className="admin-card-name">{user.username} #{user.id}</span> 
                                        {user.ip_banned && (
                                            <span className="admin-chip admin-chip--warning">
                                                <i className="fas fa-triangle-exclamation" /> IP banido
                                            </span>
                                        )}
                                        <span className="admin-chip admin-chip--role">
                                            <i className="fas fa-user" /> {user.is_admin ? 'Admin' : 'User'}
                                        </span>
                                    </div>
                                    <span className="admin-card-sub">{user.email}</span>
                                    <span className="admin-card-sub">{fmtDateTimezone(user.created_at)}</span>
                                </div>

                                <StatusPill color={banned ? 'red' : 'green'} pulse={!banned}>
                                    {banned ? 'Banido' : 'Ativo'}
                                </StatusPill>
                            </div>

                            <div className="admin-card-stats">
                                <div className="admin-stat">
                                    <span className="admin-stat-label">Faturamento</span>
                                    <span className="admin-stat-value is-green">{fmtBRL(user.faturamento)}</span>
                                    <span className="admin-stat-sub">{user.vendas || 0} vendas</span>
                                </div>

                                <div className="admin-stat">
                                    <span className="admin-stat-label">Comissão</span>
                                    <span className="admin-stat-value is-amber">{fmtBRL(user.comissao)}</span>
                                </div>

                                <div className="admin-stat">
                                    <span className="admin-stat-label">Recursos</span>
                                    <span className="admin-stat-value is-purple">
                                        <i className="fas fa-robot" /> {user.bots_count || 0}
                                        {'  '}
                                        <i className="fas fa-diagram-project" /> {user.flows_count || 0}
                                    </span>
                                </div>

                                <div className="admin-stat">
                                    <span className="admin-stat-label">Taxa</span>
                                    {editingFeeId === user.id ? (
                                        <form
                                            className="admin-fee-form"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleFeeChange(user.id, new FormData(e.target).get("fee"));
                                            }}
                                        >
                                            <input
                                                type="number"
                                                name="fee"
                                                step="0.01"
                                                defaultValue={user.fee ?? 0.5}
                                                autoFocus
                                            />
                                            <button type="submit" className="admin-icon-btn is-success" title="Salvar taxa">
                                                <i className="fas fa-check" />
                                            </button>
                                        </form>
                                    ) : (
                                        <span className="admin-stat-value is-blue">
                                            {fmtBRL(user.fee)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {(user.gateways || []).length > 0 && (
                                <div className="admin-gateways">
                                    {user.gateways.map((g) => (
                                        <span key={g} className="admin-gw-badge">
                                            <i className="fas fa-credit-card" /> {g}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="admin-card-footer">
                                <button
                                    type="button"
                                    className="admin-icon-btn is-blue"
                                    title="Alterar taxa"
                                    onClick={() => setEditingFeeId(editingFeeId === user.id ? null : user.id)}
                                >
                                    <i className="fas fa-pen" />
                                    <span>Taxa</span>
                                </button>

                                {banned ? (
                                    <button
                                        type="button"
                                        className="admin-icon-btn is-success"
                                        title="Desbanir usuário"
                                        onClick={() => handleUnban(user)}
                                        disabled={user.is_admin}
                                    >
                                        <i className="fas fa-user-check" />
                                        <span>Desbanir</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="admin-icon-btn is-danger"
                                        title="Banir usuário (bloqueia bots e IP)"
                                        onClick={() => handleBan(user)}
                                        disabled={user.is_admin}
                                    >
                                        <i className="fas fa-ban" />
                                        <span>Banir</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {users.length === 0 && !loading && (
                <div className="empty-state">
                    <i className="fas fa-users" />
                    <h4>Nenhum usuário encontrado</h4>
                </div>
            )}

            {/* PAGINAÇÃO */}
            {!loading && users.length > 0 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {currentPage} de {pagination.pages} (
                        {pagination.total} usuários)
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => loadUsers(currentPage - 1)}
                            disabled={!pagination.has_prev}
                            className="btn-secondary"
                        >
                            <i className="fas fa-chevron-left"></i> Anterior
                        </button>

                        <button
                            onClick={() => loadUsers(currentPage + 1)}
                            disabled={!pagination.has_next}
                            className="btn-primary">
                            Próximo <i className="fas fa-chevron-right"></i>

                        </button>
                    </div>
                </div>
            )
            }
        </>
    );
}
